import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import IncidentMap from "../components/IncidentMap";
import IncidentCard from "../components/IncidentCard";
import AlertBanner from "../components/AlertBanner";
import StatsStrip from "../components/StatsStrip";
import RoadAlertsPanel from "../components/RoadAlertsPanel";
import EmRoadClosuresCard from "../components/EmRoadClosuresCard";
import AircraftCard from "../components/AircraftCard";
import MountainPassPanel from "../components/MountainPassPanel";
import ProximityBanner from "../components/ProximityBanner";
import SavedLocationsManager from "../components/SavedLocationsManager";
import StaleDataBanner from "../components/StaleDataBanner";
import EvacAlertsCard from "../components/EvacAlertsCard";
import SheriffEvacCard from "../components/SheriffEvacCard";
import DebrisFlowCard from "../components/DebrisFlowCard";
import EvacAlertFormModal from "../components/EvacAlertFormModal";
import BurnBanCard from "../components/BurnBanCard";
import NifcBriefingCard from "../components/NifcBriefingCard";
import NwccBriefCard from "../components/NwccBriefCard";
import LightningCard from "../components/LightningCard";
import NifcIncidentsCard from "../components/NifcIncidentsCard";
import RxBurnsCard from "../components/RxBurnsCard";
import BurningTodayStrip from "../components/BurningTodayStrip";
import StatewideFiresPill from "../components/StatewideFiresPill";
import ViirsHotspotsCard from "../components/ViirsHotspotsCard";
import DnrFiresCard from "../components/DnrFiresCard";
import BcFiresCard from "../components/BcFiresCard";
import { dedupeWildcadAgainstDnr, sameFire, matchPerimeter, bestFireAcres, normName } from "../lib/dedupe";
import ScannerFeedsCard from "../components/ScannerFeedsCard";
import EmailVerificationBanner from "../components/EmailVerificationBanner";
import OnboardingCard from "../components/OnboardingCard";
import PushOptInCTA from "../components/PushOptInCTA";
import VerifiedReporterCTA from "../components/VerifiedReporterCTA";
import FollowedFiresPanel from "../components/FollowedFiresPanel";
import TourPrompt from "../components/TourPrompt";
import WelcomeVideoModal from "../components/WelcomeVideoModal";
import WhatsNewBanner from "../components/WhatsNewBanner";
import { useFollowedFires, enrichEdgeDistances } from "../lib/followedFires";
import { findConvergingFires } from "../lib/convergence";
import {
  fetchIncidents, fetchAlerts, fetchStats,
  fetchRoadAlerts, fetchMountainPasses, fetchCameras, refreshNow,
  fetchBcFires, fetchBcEvacs,
  fetchEvacuationZones, fetchNifcIncidents, fetchViirsHotspots, fetchDnrFires, fetchDnrBurnRequests, fetchRxBurnUnits,
  fetchEvacAlerts, fetchRoadClosures, fetchAircraft, fetchAcresHistoryBulk,
} from "../lib/api";
import { fetchFirePerimeters } from "../lib/proximity";
import { api } from "../lib/api";
import { fetchHealth } from "../lib/health";
import { classifyType, relTime } from "../lib/incidents";
import { MagnifyingGlass, Funnel, X, List, MapTrifold, FireSimple, RoadHorizon, SquaresFour, TrendUp } from "@phosphor-icons/react";
import { useCounty } from "../lib/CountyContext";
import { useAuth } from "../lib/AuthContext";
import InfoPinFormModal from "../components/InfoPinFormModal";
import { fetchInfoPins, deleteInfoPin, updateInfoPin } from "../lib/api";

const TYPE_FILTERS = [
  { key: "WILDFIRE", label: "Fires" },
  { key: "SMOKE", label: "Smoke" },
  { key: "PRESCRIBED", label: "Rx Burns" },
  { key: "OTHER", label: "Other" },
];

const ALL_TYPE_KEYS = TYPE_FILTERS.map((t) => t.key);

const STATUS_FILTERS = [
  { key: "ALL", label: "Any" },
  { key: "ACTIVE", label: "Active" },
  { key: "CONTAINED", label: "Contained" },
  { key: "OUT", label: "Out" },
];

export default function Dashboard() {
  const { county, current } = useCounty();
  const { user } = useAuth();
  const canManagePins = Boolean(user && (user.role === "admin" || user.verified));
  const statewide = county === "washington" || county === "bcborder";
  const { fires: followedFiresRaw, removeLocal: removeFollowedLocal } = useFollowedFires();
  const [showFollowedOnMap, setShowFollowedOnMap] = useState(
    () => localStorage.getItem("fw_followed_layer") !== "0"
  );
  const toggleFollowedOnMap = () =>
    setShowFollowedOnMap((v) => {
      localStorage.setItem("fw_followed_layer", v ? "0" : "1");
      return !v;
    });
  const [incidents, setIncidents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [roadAlerts, setRoadAlerts] = useState([]);
  const [passes, setPasses] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [evacFocus, setEvacFocus] = useState(null);
  const [closureFocus, setClosureFocus] = useState(null);
  const [wsdotFocus, setWsdotFocus] = useState(null);
  const [nifcHoverId, setNifcHoverId] = useState(null);
  const [dnrHoverId, setDnrHoverId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const scrollFeedCard = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };
  const handleIncidentMarkerHover = (id) => {
    setHoverId(id);
    if (id) scrollFeedCard(`incident-card-${id}`);
  };
  const handleNifcMarkerHover = (id) => {
    setNifcHoverId(id);
  };
  const handleDnrMarkerHover = (id) => {
    setDnrHoverId(id);
  };
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState(() => new Set(["WILDFIRE", "SMOKE"]));
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [trendingOnly, setTrendingOnly] = useState(false);
  const [showCameras, setShowCameras] = useState(true);
  const [mobileView, setMobileView] = useState("map");
  const [perimeters, setPerimeters] = useState([]);
  const [acresHist, setAcresHist] = useState({});
  const [infoPins, setInfoPins] = useState([]);
  const [pinFormOpen, setPinFormOpen] = useState(false);
  const [editingPin, setEditingPin] = useState(null);
  const [pinPreset, setPinPreset] = useState(null);
  const [dropPinMode, setDropPinMode] = useState(false);
  const [pinDropPoint, setPinDropPoint] = useState(null);
  const followedFires = useMemo(
    () => enrichEdgeDistances(followedFiresRaw, perimeters),
    [followedFiresRaw, perimeters],
  );
  const followedIds = useMemo(
    () => new Set(followedFires.map((f) => f.id)),
    [followedFires],
  );
  const convergences = useMemo(() => findConvergingFires(perimeters), [perimeters]);
  const convergingNames = useMemo(() => {
    const s = new Set();
    convergences.forEach((c) => {
      s.add(normName(c.nameA));
      s.add(normName(c.nameB));
    });
    return s;
  }, [convergences]);
  const [savedOpen, setSavedOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [evacZones, setEvacZones] = useState(null);
  const [nifcIncidents, setNifcIncidents] = useState([]);
  const [nifcStatewide, setNifcStatewide] = useState([]);
  const [rxBurns, setRxBurns] = useState([]);
  const [dnrBurns, setDnrBurns] = useState([]);
  const [rxBurnUnits, setRxBurnUnits] = useState(null);
  const [incidentsAll, setIncidentsAll] = useState([]);
  const [viirsHotspots, setViirsHotspots] = useState([]);
  const [frpHeatmap, setFrpHeatmap] = useState(false);
  const [dnrFires, setDnrFires] = useState([]);
  const [statewideAlerts, setStatewideAlerts] = useState([]);
  const [sidebarTab, setSidebarTab] = useState(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("brief") ? "more" : "fires"
  );
  const [sheriffAlertsAll, setSheriffAlertsAll] = useState([]);
  const sheriffAlerts = useMemo(
    () => sheriffAlertsAll.filter((a) => a.active !== false),
    [sheriffAlertsAll],
  );
  const [shareLogs, setShareLogs] = useState({});

  const [lightningFires, setLightningFires] = useState({});

  useEffect(() => {
    const loadLf = () =>
      api.get("/lightning-fires")
        .then((r) => setLightningFires(r.data.flags || {}))
        .catch(() => {});
    loadLf();
    const t = setInterval(loadLf, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/metrics/share-logs")
      .then((r) => setShareLogs(r.data.logs || {}))
      .catch(() => {});
    const onShared = (e) => {
      const { incident_id, at } = e.detail || {};
      if (incident_id) setShareLogs((prev) => ({ ...prev, [incident_id]: at }));
    };
    window.addEventListener("fw-share-logged", onShared);
    return () => window.removeEventListener("fw-share-logged", onShared);
  }, []);
  const [emClosures, setEmClosures] = useState([]);
  const [airData, setAirData] = useState(null);
  const [evacFormOpen, setEvacFormOpen] = useState(false);
  const [bcFires, setBcFires] = useState([]);
  const [bcEvacs, setBcEvacs] = useState([]);

  // BC border region data (BCWS fires + EmergencyInfoBC evacs)
  useEffect(() => {
    if (county !== "bcborder") {
      setBcFires([]);
      setBcEvacs([]);
      return undefined;
    }
    let alive = true;
    const loadBc = () => {
      fetchBcFires().then((d) => { if (alive) setBcFires(d); }).catch(() => {});
      fetchBcEvacs().then((d) => { if (alive) setBcEvacs(d); }).catch(() => {});
    };
    loadBc();
    const id = setInterval(loadBc, 120000);
    return () => { alive = false; clearInterval(id); };
  }, [county]);

  const retryRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const [inc, alr, st, rd, mp, cm, fp, hl, ez, nifc, nifcAll, rx, dnrRx, rxUnits, viirs, dnr, alrAll, sev, emrc, ah, pins, incAll] = await Promise.all([
        fetchIncidents({ county, days: 14 }).catch(() => null),
        fetchAlerts(county).catch(() => []),
        fetchStats(county).catch(() => null),
        fetchRoadAlerts(county).catch(() => []),
        fetchMountainPasses(county).catch(() => []),
        fetchCameras(county).catch(() => []),
        fetchFirePerimeters("all", true).catch(() => []),
        fetchHealth().catch(() => null),
        fetchEvacuationZones(county).catch(() => null),
        fetchNifcIncidents({ county }).catch(() => []),
        fetchNifcIncidents({ type_category: "WF", active_only: true }).catch(() => []),
        fetchNifcIncidents({ type_category: "RX", active_only: true }).catch(() => []),
        fetchDnrBurnRequests().catch(() => []),
        fetchRxBurnUnits().catch(() => null),
        fetchViirsHotspots({ hours: 24 }).catch(() => []),
        fetchDnrFires({ days: 14, county }).catch(() => []),
        fetchAlerts().catch(() => []),
        fetchEvacAlerts({ include_historical: true }).catch(() => []),
        fetchRoadClosures(county).catch(() => []),
        fetchAcresHistoryBulk(7).catch(() => ({})),
        fetchInfoPins().catch(() => []),
        county === "washington"
          ? Promise.resolve(null)
          : fetchIncidents({ county: "washington", days: 14 }).catch(() => []),
      ]);
      if (inc === null) {
        // Incidents fetch failed (e.g. timeout) — keep what's on screen and retry shortly.
        if (!retryRef.current) {
          retryRef.current = setTimeout(() => { retryRef.current = null; load(); }, 5000);
        }
      } else {
        if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
        setIncidents(inc);
      }
      setAlerts(alr);
      if (st) setStats(st);
      setRoadAlerts(rd);
      setPasses(mp);
      setCameras(cm);
      setPerimeters(fp);
      setHealth(hl);
      setEvacZones(ez);
      setNifcIncidents(nifc);
      setNifcStatewide(nifcAll);
      setRxBurns(rx);
      setDnrBurns(dnrRx);
      if (rxUnits) setRxBurnUnits(rxUnits);
      setIncidentsAll(incAll === null ? (inc || []) : (incAll || []));
      setViirsHotspots(viirs);
      setDnrFires(dnr);
      setStatewideAlerts(alrAll);
      // County tags on Perimeter evacs are unreliable near county lines —
      // filter by geography (county bounds + ~25mi margin) instead of tag.
      const b = current?.bounds;
      setSheriffAlertsAll(
        b
          ? sev.filter(
              (a) =>
                a.latitude == null ||
                (a.latitude >= b.min_lat - 0.4 &&
                  a.latitude <= b.max_lat + 0.4 &&
                  a.longitude >= b.min_lon - 0.55 &&
                  a.longitude <= b.max_lon + 0.55),
            )
          : sev,
      );
      setEmClosures(emrc);
      setAcresHist(ah || {});
      setInfoPins(pins || []);
    } catch (e) {
      console.error("Load failed", e);
      if (!retryRef.current) {
        retryRef.current = setTimeout(() => { retryRef.current = null; load(); }, 5000);
      }
    } finally {
      setLoading(false);
    }
  }, [county]);

  useEffect(() => {
    setLoading(true);
    setFocus(null);
    setActiveId(null);
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => {
      clearInterval(id);
      if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
    };
  }, [load]);

  // "View on Map" deep-link from incident pages: /?flyto=lat,lon&inc=id
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get("flyto");
    if (!f) return;
    const [la, lo] = f.split(",").map(Number);
    if (Number.isFinite(la) && Number.isFinite(lo)) {
      setFocus([la, lo]);
      const inc = searchParams.get("inc");
      if (inc) setActiveId(inc);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("flyto");
    next.delete("inc");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Live aircraft poll (15 s) — separate from the main 5-min refresh
  useEffect(() => {
    let stop = false;
    const poll = () =>
      fetchAircraft(county)
        .then((d) => { if (!stop) setAirData(d); })
        .catch(() => {});
    poll();
    const t = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(t); };
  }, [county]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshNow();
      await new Promise((r) => setTimeout(r, 1500));
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const allTypesSelected = typeFilter.size === ALL_TYPE_KEYS.length;
  const toggleType = (key) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      // Empty set behaves like "all" so the user never gets a zero-results trap
      if (next.size === 0) return new Set(ALL_TYPE_KEYS);
      return next;
    });
  };
  const toggleAllTypes = () => {
    setTypeFilter(allTypesSelected ? new Set(["WILDFIRE", "SMOKE"]) : new Set(ALL_TYPE_KEYS));
  };

  // DNR fires with recorded activity in the last 10 days (map + dedupe basis).
  const recentDnrFires = useMemo(() => {
    const cutoff = Date.now() - 10 * 24 * 3600 * 1000;
    const recent = (iso) => iso && new Date(iso).getTime() >= cutoff;
    return dnrFires.filter(
      (f) => recent(f.discovered_at) || recent(f.controlled_at) || recent(f.out_at)
    );
  }, [dnrFires]);

  // Map dots: only fires NOT yet contained/controlled or out (user rule).
  // The full recent list above still drives dedupe so WildCAD/NIFC markers
  // stacked on a controlled DNR fire keep working.
  const mapDnrFires = useMemo(
    () => dnrFires.filter((f) => !f.controlled_at && !f.out_at),
    [dnrFires],
  );

  // When a WildCAD incident duplicates a recent DNR fire (same fire, two
  // agencies), show the DNR/NIFC record and hide the WildCAD entry. Uses the
  // RAW recent DNR list so the rule still applies when NIFC also covers the
  // fire (NIFC dedupe removes the DNR map dot but the fire stays visible).
  const dnrDedupedIncidents = useMemo(
    () => dedupeWildcadAgainstDnr(incidents, recentDnrFires),
    [incidents, recentDnrFires],
  );

  // Map bubbles: county NIFC points + statewide actives (perimeter fires
  // outside the county keep their red bubble). Deduped by id.
  // Per user rule: hide NIFC markers on new fires until GIS reports
  // acreage >= 0.10 (WildCAD/DNR still cover fresh starts).
  const mapNifc = useMemo(() => {
    const seen = new Set();
    return [...nifcIncidents, ...nifcStatewide].filter((n) => {
      if (!n.id || seen.has(n.id)) return false;
      seen.add(n.id);
      // Prescribed burns render via the dedicated Rx Burns layer (opt-in toggle)
      if (n.type_category === "RX") return false;
      if (n.incident_size == null || n.incident_size < 0.1) return false;
      // Flame markers win on the map — drop NIFC bubbles stacked on a
      // WildCAD or DNR flame for the same fire.
      if (dnrDedupedIncidents.some((i) => sameFire(i, n))) return false;
      return !recentDnrFires.some((f) => sameFire(f, n));
    });
  }, [nifcIncidents, nifcStatewide, dnrDedupedIncidents, recentDnrFires]);

  // Best-known acreage per feed card (mapped perimeter > NIFC > WildCAD) —
  // dispatch acreage lags well behind GIS for growing fires.
  const nifcAll = useMemo(
    () => [...nifcIncidents, ...nifcStatewide],
    [nifcIncidents, nifcStatewide],
  );

  // Fires used to group the Sheriff/EM evac panel by incident.
  const evacGroupFires = useMemo(
    () => [...dnrDedupedIncidents, ...nifcAll, ...recentDnrFires],
    [dnrDedupedIncidents, nifcAll, recentDnrFires],
  );

  // Fires that grew in the last 24h (WildCAD + matched NIFC acreage points)
  const trendingIds = useMemo(() => {
    const cutoff = Date.now() - 24 * 3600 * 1000;
    const ids = new Set();
    for (const i of dnrDedupedIncidents) {
      const nifcMatch = nifcAll.find((n) => sameFire(i, n));
      const pts = [
        ...(acresHist[i.id] || []),
        ...(nifcMatch ? acresHist[nifcMatch.id] || [] : []),
      ]
        .filter((p) => p.acres != null)
        .sort((a, b) => (a.at || "").localeCompare(b.at || ""));
      if (pts.length < 2) continue;
      const last = pts[pts.length - 1];
      if (new Date(last.at).getTime() < cutoff) continue;
      let baseline = null;
      for (const p of pts) {
        if (new Date(p.at).getTime() <= cutoff) baseline = p;
      }
      if (!baseline) baseline = pts[0];
      if (last.acres > baseline.acres) ids.add(i.id);
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnrDedupedIncidents, nifcAll, acresHist]);

  const filtered = dnrDedupedIncidents.filter((i) => {
    if (!typeFilter.has(classifyType(i.type))) return false;
    if (statusFilter !== "ALL" && i.status_label !== statusFilter) return false;
    if (trendingOnly && !trendingIds.has(i.id)) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${i.name} ${i.location || ""} ${i.inc_num || ""} ${i.type}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const bestAcresById = useMemo(() => {
    const out = {};
    for (const i of filtered) {
      if (i.latitude == null) continue;
      const nifcMatch = nifcAll.find((n) => sameFire(i, n));
      const perim = matchPerimeter(i, perimeters);
      const best = bestFireAcres(i, nifcMatch, perim);
      if (best && best.source !== "wildcad") out[i.id] = best;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, nifcAll, perimeters]);

  // 7-day acreage points per feed card (WildCAD + matched NIFC records)
  const sparkById = useMemo(() => {
    const out = {};
    for (const i of filtered) {
      const nifcMatch = nifcAll.find((n) => sameFire(i, n));
      const pts = [
        ...(acresHist[i.id] || []),
        ...(nifcMatch ? acresHist[nifcMatch.id] || [] : []),
      ]
        .filter((p) => p.acres != null)
        .sort((a, b) => (a.at || "").localeCompare(b.at || ""));
      if (pts.length >= 2) out[i.id] = pts;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, nifcAll, acresHist]);

  // Marker click: highlight the feed card only — never move/zoom the map
  // (the popup opens in place; flying resets the user's chosen zoom).
  const handleSelect = (id) => {
    setActiveId(id);
    if (id) scrollFeedCard(`incident-card-${id}`);
  };


  // Passes with an active closure/restriction — drives the red badge on the
  // DOT tab so closures are visible even while on the Fires tab.
  const NO_RESTRICTION = /no\s+(current\s+information|restriction)/i;
  const passIssueCount = passes.filter(
    (p) =>
      (p.road_condition && /closed|closure/i.test(p.road_condition)) ||
      (p.restriction_north && !NO_RESTRICTION.test(p.restriction_north)) ||
      (p.restriction_south && !NO_RESTRICTION.test(p.restriction_south)) ||
      Boolean(p.travel_advisory),
  ).length;

  const rootRef = useRef(null);
  useEffect(() => {
    const update = () => {
      const el = rootRef.current;
      if (!el) return;
      el.style.height = `${window.innerHeight - el.getBoundingClientRect().top}px`;
    };
    update();
    const t1 = setTimeout(update, 500);
    const t2 = setTimeout(update, 1500);
    const t3 = setTimeout(update, 3000);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    const ro = new ResizeObserver(() => requestAnimationFrame(update));
    ro.observe(document.body);
    if (rootRef.current?.parentElement) ro.observe(rootRef.current.parentElement);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={rootRef} className="flex flex-col h-[calc(100dvh-101px)] md:h-[calc(100dvh-56px)] overflow-hidden">
      <TourPrompt />
      <WelcomeVideoModal />
      <SavedLocationsManager open={savedOpen} onClose={() => setSavedOpen(false)} />
      <InfoPinFormModal
        open={pinFormOpen}
        onClose={() => { setPinFormOpen(false); setPinDropPoint(null); setPinPreset(null); }}
        pin={editingPin}
        initialPoint={pinDropPoint}
        preset={pinPreset}
        center={
          current?.bounds
            ? [
                (current.bounds.min_lat + current.bounds.max_lat) / 2,
                (current.bounds.min_lon + current.bounds.max_lon) / 2,
              ]
            : undefined
        }
        onSaved={(p) =>
          setInfoPins((prev) => {
            const i = prev.findIndex((x) => x.id === p.id);
            if (i >= 0) { const next = [...prev]; next[i] = p; return next; }
            return [p, ...prev];
          })
        }
      />
      <EvacAlertFormModal
        open={evacFormOpen}
        onClose={() => setEvacFormOpen(false)}
        onCreated={(a) => {
          setSheriffAlertsAll((prev) => [a, ...prev]);
          load();
        }}
        center={
          current?.bounds
            ? [
                (current.bounds.min_lat + current.bounds.max_lat) / 2,
                (current.bounds.min_lon + current.bounds.max_lon) / 2,
              ]
            : undefined
        }
      />

      <div className="md:hidden flex border-b border-tac-border bg-tac-surface/60">
        <button
          data-testid="mobile-view-map"
          onClick={() => setMobileView("map")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-semibold uppercase tracking-wider ${
            mobileView === "map" ? "text-tac-primary border-b-2 border-tac-primary" : "text-tac-dim"
          }`}
        >
          <MapTrifold size={14} weight={mobileView === "map" ? "fill" : "regular"} /> Map
        </button>
        <button
          data-testid="mobile-view-list"
          onClick={() => setMobileView("list")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-semibold uppercase tracking-wider ${
            mobileView === "list" ? "text-tac-primary border-b-2 border-tac-primary" : "text-tac-dim"
          }`}
        >
          <List size={14} weight={mobileView === "list" ? "fill" : "regular"} /> Feed ({filtered.length})
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside
          data-testid="feed-sidebar"
          className={`${
            mobileView === "list" ? "flex" : "hidden"
          } md:flex flex-col w-full md:w-[420px] lg:w-[460px] md:shrink-0 border-r border-tac-border bg-tac-bg overflow-y-auto tac-scroll`}
        >
          <div className="px-4 py-2 border-b border-tac-border flex items-center justify-between">
            <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
              {current?.name}{statewide ? "" : " County"}
            </div>
            {stats?.last_refresh && (
              <span className="text-[15px] font-mono-tac text-tac-dim">
                {relTime(stats.last_refresh)}
              </span>
            )}
          </div>

          <div className="flex border-b border-tac-border bg-tac-surface/40" data-testid="sidebar-tabs">
            {[
              { key: "fires", label: "Fires", icon: FireSimple, count: filtered.length },
              { key: "dot", label: "DOT", icon: RoadHorizon, count: roadAlerts.length, alert: passIssueCount + emClosures.length },
              { key: "more", label: "More", icon: SquaresFour, count: null, alert: sheriffAlerts.length },
            ].map((t) => {
              const active = sidebarTab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`sidebar-tab-${t.key}`}
                  onClick={() => setSidebarTab(t.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-bold uppercase tracking-wider border-b-2 transition-colors ${
                    active
                      ? "text-tac-primary border-tac-primary bg-tac-surface/60"
                      : "text-tac-dim border-transparent hover:text-tac-text"
                  }`}
                >
                  <Icon size={13} weight={active ? "fill" : "regular"} /> {t.label}
                  {t.count != null && t.count > 0 && (
                    <span className="font-mono-tac tabular text-[10px] text-tac-faint">
                      {t.count}
                    </span>
                  )}
                  {t.alert > 0 && (
                    <span
                      data-testid={`${t.key}-tab-alert-badge`}
                      title={
                        t.key === "dot"
                          ? `${t.alert} pass${t.alert === 1 ? "" : "es"} with closures/restrictions`
                          : `${t.alert} active evacuation alert${t.alert === 1 ? "" : "s"}`
                      }
                      className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-tac-danger text-white text-[10px] font-bold leading-none"
                    >
                      {t.alert}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {sidebarTab === "fires" && (
            <>
              <div className="p-3 border-b border-tac-border space-y-2.5">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-tac-faint" />
              <input
                data-testid="incident-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search incidents..."
                className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm pl-8 pr-8 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none transition-colors"
              />
              {query && (
                <button
                  data-testid="search-clear"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-tac-faint hover:text-tac-text"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="flex gap-1 flex-wrap">
              <button
                data-testid="type-filter-all"
                onClick={toggleAllTypes}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  allTypesSelected
                    ? "bg-tac-primary text-white border-tac-primary"
                    : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text hover:border-tac-borderBright"
                }`}
              >
                All
              </button>
              {TYPE_FILTERS.map((t) => {
                const active = typeFilter.has(t.key);
                return (
                  <button
                    key={t.key}
                    data-testid={`type-filter-${t.key.toLowerCase()}`}
                    onClick={() => toggleType(t.key)}
                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                      active
                        ? "bg-tac-primary text-white border-tac-primary"
                        : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text hover:border-tac-borderBright"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1 flex-wrap items-center justify-between">
              <div className="flex gap-1 flex-wrap">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`status-filter-${s.key.toLowerCase()}`}
                    onClick={() => setStatusFilter(s.key)}
                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                      statusFilter === s.key
                        ? "bg-tac-surface2 text-tac-primary border-tac-primary"
                        : "bg-tac-surface text-tac-faint border-tac-border hover:text-tac-text"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="trending-filter-btn"
                  title="Show only fires that grew in the last 24 hours"
                  onClick={() => setTrendingOnly((v) => !v)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                    trendingOnly
                      ? "bg-tac-danger/15 text-tac-danger border-tac-danger"
                      : "bg-tac-surface text-tac-faint border-tac-border hover:text-tac-text"
                  }`}
                >
                  <TrendUp size={11} weight="bold" />
                  Trending{trendingIds.size > 0 ? ` ${trendingIds.size}` : ""}
                </button>
              </div>
              <label className="flex items-center gap-1.5 text-[15px] font-bold uppercase tracking-wider text-tac-dim cursor-pointer">
                <input
                  data-testid="toggle-cameras"
                  type="checkbox"
                  checked={showCameras}
                  onChange={(e) => setShowCameras(e.target.checked)}
                  className="accent-tac-primary"
                />
                Cams
              </label>
            </div>
          </div>

          <FollowedFiresPanel
            fires={followedFires}
            onRemoved={removeFollowedLocal}
            showOnMap={showFollowedOnMap}
            onToggleShowOnMap={toggleFollowedOnMap}
            evacAlerts={sheriffAlerts}
            shareLogs={shareLogs}
          />

          <BurningTodayStrip
            dnrBurns={dnrBurns}
            onFlyTo={(lat, lon) => {
              setFocus([lat, lon]);
              setMobileView("map");
            }}
          />

          <div className="px-4 py-2 flex items-center justify-between border-b border-tac-border">
            <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
              Incident Feed
            </div>
            <div className="text-[15px] font-mono-tac tabular text-tac-dim">
              {filtered.length} / {incidents.length}
            </div>
          </div>

          {loading && (
            <>
              <div className="scan-line" />
              <div className="p-6 text-center text-[15px] text-tac-dim">Loading dispatch feed…</div>
            </>
          )}

          <div>
            {!loading && filtered.length === 0 && (
              <div className="p-10 text-center text-[15px] text-tac-faint">
                <Funnel size={28} weight="duotone" className="mx-auto mb-3 text-tac-borderBright" />
                No incidents in {current?.name}{statewide ? "" : " County"} match your filters.
              </div>
            )}
            {filtered.map((inc) => (
              <IncidentCard
                key={inc.id}
                incident={inc}
                bestAcres={bestAcresById[inc.id]}
                history={sparkById[inc.id]}
                converging={convergingNames.has(normName(inc.name))}
                active={inc.id === activeId || inc.id === hoverId}
                onHover={(id) => setHoverId(id)}
                evacAlerts={sheriffAlerts}
                lastSharedAt={shareLogs[inc.id] || null}
                lightning={lightningFires[inc.id] || null}
                followed={followedIds.has(inc.id)}
              />
            ))}
          </div>

          <StatewideFiresPill
            incidents={nifcStatewide}
            onFlyTo={(lat, lon) => setFocus([lat, lon])}
          />

          <NifcIncidentsCard
            incidents={nifcIncidents}
            onFlyTo={(lat, lon) => setFocus([lat, lon])}
            onHover={setNifcHoverId}
            hoverId={nifcHoverId}
            hoverPool={mapNifc}
          />
          <RxBurnsCard
            burns={rxBurns}
            dnrBurns={dnrBurns}
            countyName={current?.name}
            onFlyTo={(lat, lon) => {
              setFocus([lat, lon]);
              setMobileView("map");
            }}
          />
          <ViirsHotspotsCard
            hotspots={viirsHotspots}
            onFlyTo={(lat, lon) => setFocus([lat, lon])}
            frpHeatmap={frpHeatmap}
            onToggleHeatmap={() => setFrpHeatmap((v) => !v)}
          />
          {county === "bcborder" && <BcFiresCard fires={bcFires} evacs={bcEvacs} />}
          <DnrFiresCard
            fires={dnrFires}
            onFlyTo={(lat, lon) => setFocus([lat, lon])}
            onHover={setDnrHoverId}
            hoverId={dnrHoverId}
          />
          <AircraftCard
            data={airData}
            onFlyTo={(lat, lon) => setFocus([lat, lon])}
          />
            </>
          )}

          {sidebarTab === "dot" && (
            <>
              <EmRoadClosuresCard
                closures={emClosures}
                onSelect={(c) => {
                  setFocus([c.latitude, c.longitude]);
                  setClosureFocus({ id: c.id, ts: Date.now() });
                  setMobileView("map");
                }}
              />
              {roadAlerts.length === 0 && passes.length === 0 && emClosures.length === 0 && (
                <div className="p-10 text-center text-[15px] text-tac-faint" data-testid="dot-empty-state">
                  <RoadHorizon size={28} weight="duotone" className="mx-auto mb-3 text-tac-borderBright" />
                  No WSDOT road alerts or pass reports for {current?.name}{statewide ? "" : " County"} right now.
                </div>
              )}
              <RoadAlertsPanel alerts={roadAlerts} />
              <MountainPassPanel passes={passes} />
            </>
          )}

          {sidebarTab === "more" && (
            <>
              <SheriffEvacCard
                alerts={sheriffAlertsAll}
                closures={emClosures}
                wsdotAlerts={roadAlerts}
                fires={evacGroupFires}
                onChanged={load}
                onPost={() => setEvacFormOpen(true)}
                onFlyTo={(lat, lon, id) =>
                  id ? setEvacFocus({ id, ts: Date.now() }) : setFocus([lat, lon])
                }
                onClosureSelect={(c) => {
                  setFocus([c.lat, c.lon]);
                  if (c.src === "WSDOT") setWsdotFocus({ id: c.id, ts: Date.now() });
                  else setClosureFocus({ id: c.id, ts: Date.now() });
                  setMobileView("map");
                }}
              />
              <EvacAlertsCard evacuationZones={evacZones} />
              <DebrisFlowCard county={statewide ? null : county} />
              <NwccBriefCard />
              <NifcBriefingCard />
              {!statewide && <BurnBanCard countySlug={county} countyName={current?.name} />}
              <LightningCard countyName={current?.name} bounds={current?.bounds} />
              <StatsStrip stats={stats} />
            </>
          )}

          <StaleDataBanner county={current} health={health} />
          <EmailVerificationBanner />
          <PushOptInCTA />
          <VerifiedReporterCTA />
          <OnboardingCard onAddLocation={() => setSavedOpen(true)} />
        </aside>

        <main
          data-testid="map-area"
          className={`${mobileView === "map" ? "flex" : "hidden"} md:flex flex-col flex-1 min-h-0 min-w-0 bg-tac-bg`}
        >
          <WhatsNewBanner />
          <div className="flex-1 min-h-0 relative">
            <IncidentMap
              incidents={filtered}
              activeId={hoverId || activeId}
              onMarkerClick={handleSelect}
              focus={focus}
              cameras={showCameras ? cameras : []}
              perimeters={perimeters}
              evacuationZones={evacZones}
              nifcIncidents={mapNifc}
              nifcAll={nifcAll}
              rxBurns={rxBurns}
              dnrBurnRequests={dnrBurns}
              rxBurnUnits={rxBurnUnits}
              viirsHotspots={viirsHotspots}
              viirsFrpHeatmap={frpHeatmap}
              dnrFires={mapDnrFires}
              weatherAlerts={statewideAlerts}
              sheriffEvacAlerts={sheriffAlerts}
              evacFocus={evacFocus}
              closureFocus={closureFocus}
              wsdotFocus={wsdotFocus}
              nifcHoverId={nifcHoverId}
              dnrHoverId={dnrHoverId}
              onIncidentMarkerHover={handleIncidentMarkerHover}
              onNifcMarkerHover={handleNifcMarkerHover}
              onDnrMarkerHover={handleDnrMarkerHover}
              infoPins={infoPins}
              canManagePins={canManagePins}
              onAddPin={() => {
                setEditingPin(null);
                if (user?.role === "admin") {
                  setDropPinMode(true);
                  setMobileView("map");
                } else {
                  setPinFormOpen(true);
                }
              }}
              onQuickDebris={() => {
                setEditingPin(null);
                setPinPreset("debris");
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      setPinDropPoint([pos.coords.latitude, pos.coords.longitude]);
                      setPinFormOpen(true);
                    },
                    () => setPinFormOpen(true),
                    { enableHighAccuracy: true, timeout: 8000 }
                  );
                } else {
                  setPinFormOpen(true);
                }
              }}
              draggablePins={user?.role === "admin"}
              onMovePin={async (p, lat, lon) => {
                try {
                  const saved = await updateInfoPin(p.id, { latitude: lat, longitude: lon });
                  setInfoPins((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
                } catch {
                  setInfoPins((prev) => [...prev]);
                }
              }}
              dropPinActive={dropPinMode}
              onDropPinConfirm={(pt) => {
                setDropPinMode(false);
                setPinDropPoint(pt);
                setPinFormOpen(true);
              }}
              onDropPinCancel={() => setDropPinMode(false)}
              onEditPin={(p) => { setEditingPin(p); setPinFormOpen(true); }}
              onDeletePin={async (p) => {
                try {
                  await deleteInfoPin(p.id);
                  setInfoPins((prev) => prev.filter((x) => x.id !== p.id));
                } catch { /* forbidden or gone */ }
              }}
              roadClosures={emClosures}
              wsdotAlerts={roadAlerts}
              passes={passes}
              aircraft={airData?.aircraft || []}
              bcFires={bcFires}
              bcEvacs={bcEvacs}
              followedFires={followedFires}
              showFollowed={showFollowedOnMap}
              onToggleFollowed={toggleFollowedOnMap}
              convergences={convergences}
              allIncidents={incidentsAll.length ? incidentsAll : incidents}
            />
          </div>
          <div data-testid="below-map-banners" className="shrink-0 max-h-[45vh] overflow-y-auto tac-scroll border-t border-tac-border">
            <AlertBanner alerts={alerts} />
            <ProximityBanner onOpenManager={() => setSavedOpen(true)} />
          </div>
        </main>
      </div>
    </div>
  );
}

